// Scales a byte count through B/KB/MB/GB/TB. Previously capped at MB, which
// rendered multi-gigabyte department quotas as absurd values like "76800.00 MB"
// on the real admin dashboard. The unit is intentionally Latin in both locales
// (KB/MB/GB are read as-is in Arabic pharmacy/IT usage).
//
// The number+unit is wrapped in a Unicode LTR isolate (U+2066 … U+2069) so it
// always reads "74.92 GB" even inside an RTL paragraph — without the isolate,
// the neutral space lets the two runs reorder to a confusing "GB 74.92".
// Safe because formatBytes output is display-only and never embedded into the
// QR/PDF overlay text.
const LRI = '⁦';
const PDI = '⁩';
function bidiIsolate(text: string): string {
  return `${LRI}${text}${PDI}`;
}

export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return bidiIsolate('0 B');
  if (bytes < 1024) return bidiIsolate(`${bytes} B`);
  const units = ['KB', 'MB', 'GB', 'TB'];
  let value = bytes / 1024;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  // KB to 1 decimal (matches prior behaviour), MB and above to 2 decimals.
  const decimals = unitIndex === 0 ? 1 : 2;
  return bidiIsolate(`${value.toFixed(decimals)} ${units[unitIndex]}`);
}

export function formatDistanceToNow(dateStr: string, rtl = true): string {
  const date = new Date(dateStr);
  const now  = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays  = Math.floor(diffHours / 24);

  if (rtl) {
    if (diffMins < 1)    return 'الآن';
    if (diffMins < 60)   return `منذ ${diffMins} دقيقة`;
    if (diffHours < 24)  return `منذ ${diffHours} ساعة`;
    if (diffDays < 7)    return `منذ ${diffDays} يوم`;
    return date.toLocaleDateString('ar-SA');
  } else {
    if (diffMins < 1)    return 'just now';
    if (diffMins < 60)   return `${diffMins}m ago`;
    if (diffHours < 24)  return `${diffHours}h ago`;
    if (diffDays < 7)    return `${diffDays}d ago`;
    return date.toLocaleDateString('en-US');
  }
}

export function formatDate(dateStr: string, lang: 'ar' | 'en' = 'ar'): string {
  return new Date(dateStr).toLocaleDateString(lang === 'ar' ? 'ar-SA' : 'en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
  });
}
